import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fuarService, apiErrorMessage } from '../services/fuarService';
import { saveFuarKurulumSaatleri } from '../services/fuarKurulumSaatiService';
import { applyGlobalDefaultsToFuar, getFuarHMax } from '../services/fuarKuralService';
import {
  listSalonlar, addSalonToFuar, removeSalonFromFuar, activateAllFairgroundSalons,
} from '../services/fuarAlaniSalonService';
import i18n from "../i18n";

/**
 * Backlog #192 (2026-09-26): oluşturma ekranında seçilen salonları yeni fuara bağlar. Hepsi
 * seçildiyse tek istek (activate-all, tek transaction), değilse salon salon. Fuar zaten
 * yaratılmış olduğu için bir salonun başarısızlığı fuarı geri almaz — sonuç raporlanır
 * (globalDefaults ile aynı ilke).
 */
async function linkChosenHalls(fuarId, salonIds, allSalonIds) {
  const chosen = (salonIds || []).map(String);
  if (chosen.length === 0) return { linked: 0, failed: [] };
  const all = (allSalonIds || []).map(String);
  if (all.length > 0 && chosen.length === all.length && all.every((id) => chosen.includes(id))) {
    try {
      await activateAllFairgroundSalons(fuarId);
      return { linked: chosen.length, failed: [] };
    } catch (e) {
      return { linked: 0, failed: [{ salonId: null, message: e.message }] };
    }
  }
  const failed = [];
  let linked = 0;
  for (const salonId of chosen) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await addSalonToFuar(fuarId, salonId);
      linked += 1;
    } catch (e) {
      failed.push({ salonId, message: e.message });
    }
  }
  return { linked, failed };
}

/**
 * Backlog #192 (Bilge, 2026-09-26): bir fuarın fuar alanı DEĞİŞTİKTEN sonra, yeni alanda kayıtlı
 * OLMAYAN salonların bu fuarla bağını kaldırır. Standı olan salonu backend 409 ile reddeder
 * (DELETE /fuar/{fuarId}/salon-to-fuar/{salonId}) — onlar kalır ve `refused` olarak döner.
 * İki alanda da kayıtlı (paylaşılan) salonlar hiç dokunulmadan kalır.
 */
async function unlinkOldVenueHalls(fuarId, newFuarAlaniId) {
  const [ctx, registered] = await Promise.all([
    getFuarHMax(fuarId, { forceLive: true }),
    listSalonlar(newFuarAlaniId),
  ]);
  const keep = new Set((registered || []).map((h) => String(h.salonId)));
  const removed = [];
  const refused = [];
  for (const h of ctx?.halls ?? []) {
    if (keep.has(String(h.salonId))) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await removeSalonFromFuar(fuarId, h.salonId);
      removed.push(h.salonName ?? String(h.salonId));
    } catch (e) {
      refused.push({ salonName: h.salonName ?? String(h.salonId), message: e.message });
    }
  }
  return { removed, refused };
}

/**
 * Backlog #718: the per-role Kurulum hours (PUT /fuar/{id}/kurulum-saatleri) are saved AFTER the fair itself. `roller` = only the roles that
 * changed (degisenRoller); [] / undefined = nothing to send. The fair is already saved when this runs, so a refusal (400 naming the role, nothing
 * saved) does not fail the thunk: it comes back as `{ fuarId, message }` for the form to show beside the Kurulum block.
 */
async function saveKurulumRoller(fuarId, roller) {
  if (!Array.isArray(roller) || roller.length === 0) return null;
  try {
    await saveFuarKurulumSaatleri(fuarId, roller);
    return null;
  } catch (e) {
    return { fuarId, message: apiErrorMessage(e) };
  }
}

function hallLinkNote(hallLink) {
  if (!hallLink) return '';
  const parts = [];
  if (hallLink.linked > 0) parts.push(i18n.t("services:fuarSlice.salonBuFuaraBaglandi", { linked: hallLink.linked }));
  if (hallLink.failed?.length > 0) {
    parts.push(i18n.t("services:fuarSlice.baglanamayanSalonLarSalonlarSayfasindan", { v: hallLink.failed.map((f) => f.message).join('; ') }));
  }
  return parts.length ? ` ${parts.join(' ')}` : '';
}

function venueCleanupNote(cleanup) {
  if (!cleanup) return '';
  if (cleanup.error) return i18n.t("services:fuarSlice.eskiFuarAlanininSalonBaglari", { error: cleanup.error });
  const parts = [];
  if (cleanup.removed.length > 0) parts.push(i18n.t("services:fuarSlice.eskiFuarAlanindanSalonunBagi", { length: cleanup.removed.length, v: cleanup.removed.join(', ') }));
  if (cleanup.refused.length > 0) {
    parts.push(i18n.t("services:fuarSlice.standiOlduguIcinKalanSalon", { v: cleanup.refused.map((r) => r.salonName).join(', ') }));
  }
  return parts.length ? ` ${parts.join(' ')}` : '';
}

/**
 * Async Thunks
 */
export const fetchFuarList = createAsyncThunk(
  'fuar/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      return await fuarService.getFuarList(params);
    } catch (error) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const fetchFuarDetail = createAsyncThunk(
  'fuar/fetchDetail',
  async (fuarId, { rejectWithValue }) => {
    try {
      return await fuarService.getFuarDetail(fuarId);
    } catch (error) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

/**
 * Yeni fuar oluştur.
 *
 * ⚠️ Oluşturmadan SONRA global kural şablonu bu fuara KOPYALANIR
 * (bkz. fuarKuralService.applyGlobalDefaultsToFuar ve
 * pages/Management/Projects/GlobalKurallarPage.jsx). Backend'de `fuar_kural` için
 * gerçek bir global seviye olmadığı için kural miras alınmıyor, kopyalanıyor —
 * bu yüzden kopyalamanın tetiklendiği tek an fuarın DOĞDUĞU an.
 * 2026-09-17 (#55): stand tipi kuralları artık KOPYALANMIYOR — fuar 0 satırları backend'de
 * canlı miras alınıyor. Kopyalanan: skaler kurallar, serbest kW ve kullanım kuralları.
 *
 * ⚠️ KOPYALAMA BAŞARISIZ OLURSA FUAR YİNE DE OLUŞUR. Kural şablonu bir kolaylıktır;
 *    yarısı yazılmış diye fuarın kendisini reddetmek ya da geri almak orantısız
 *    olurdu (üstelik fuar zaten yaratılmış olur). Sonuç payload'a
 *    `globalDefaults` olarak iliştiriliyor ki çağıran ekran kullanıcıya
 *    söyleyebilsin, ve elle uygulama yolu her zaman açık:
 *    Fuar Kuralları → "Global Varsayılanları Uygula".
 */
export const createFuar = createAsyncThunk(
  'fuar/create',
  // WAS: async (formData, …) — backlog #192: { formData, salonIds, allSalonIds } da kabul eder;
  // düz FormData eskisi gibi çalışır (salon bağlanmaz).
  async (arg, { rejectWithValue }) => {
    const formData = arg instanceof FormData ? arg : arg?.formData;
    const salonIds = arg instanceof FormData ? null : arg?.salonIds;
    const allSalonIds = arg instanceof FormData ? null : arg?.allSalonIds;
    const kurulumRoller = arg instanceof FormData ? null : arg?.kurulumRoller; // #718
    try {
      const created = await fuarService.addFuar(formData);
      // add-new yeni id'yi data.fuarId'de döndürüyor (backlog #18) — servis onu çıkarıyor.
      const newFuarId = created?.fuarId ?? null;
      // #718: role hours need the new fair's id, so they go right after add-new.
      const kurulumHata = newFuarId != null ? await saveKurulumRoller(newFuarId, kurulumRoller) : null;
      let globalDefaults = { applied: false, reason: i18n.t("services:fuarSlice.yeniFuarIdSiOkunamadi") };
      if (newFuarId != null) {
        try {
          // overwrite:false — yeni fuarın kendi kural seti zaten varsa (olmamalı)
          // dokunma. Kopyalama isteğe bağlı bir zenginleştirme, zorunlu bir adım değil.
          globalDefaults = await applyGlobalDefaultsToFuar(newFuarId, { overwrite: false });
        } catch (e) {
          globalDefaults = { applied: false, reason: e.message };
        }
      }
      // Backlog #192: oluşturmada seçilen salonlar, fuarın id'si belli olunca bağlanır.
      const hallLink = newFuarId != null && Array.isArray(salonIds)
        ? await linkChosenHalls(newFuarId, salonIds, allSalonIds)
        : null;
      return { ...created, globalDefaults, hallLink, kurulumHata };
    } catch (error) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const updateFuar = createAsyncThunk(
  'fuar/update',
  // WAS: async ({ id, formData }, …) → return await fuarService.updateFuar(id, formData);
  // Backlog #192: venueChange = { newFuarAlaniId } verilirse, kayıttan sonra eski fuar alanının
  // standsız salonlarının bağı kaldırılır. Temizlik başarısız olsa da güncelleme başarılı sayılır.
  async ({ id, formData, venueChange, kurulumRoller }, { rejectWithValue }) => {
    let updated;
    try {
      updated = await fuarService.updateFuar(id, formData);
    } catch (error) {
      return rejectWithValue(apiErrorMessage(error));
    }
    const kurulumHata = await saveKurulumRoller(id, kurulumRoller); // #718
    if (!venueChange?.newFuarAlaniId) return kurulumHata ? { updated, kurulumHata } : updated;
    let venueCleanup;
    try {
      venueCleanup = await unlinkOldVenueHalls(id, venueChange.newFuarAlaniId);
    } catch (e) {
      venueCleanup = { error: e.message };
    }
    return { updated, venueCleanup, kurulumHata };
  }
);

export const deleteFuar = createAsyncThunk(
  'fuar/delete',
  async (id, { rejectWithValue }) => {
    try {
      await fuarService.deleteFuar(id);
      return id;
    } catch (error) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const fetchFuarStatus = createAsyncThunk(
  'fuar/fetchStatus',
  async (fuarId, { rejectWithValue }) => {
    try {
      return await fuarService.getFuarStatus(fuarId);
    } catch (error) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

/**
 * Backlog #16/#19. Arşivleme sonrası satırı yerelde 'ARCHIVED' yapıyoruz (biliyoruz).
 * Arşivden çıkarınca hangi gruba düşeceğini (FUTURE/ACTIVE/.../PAST) yalnızca backend'in
 * tarih mantığı bilir — burada tahmin etmek yerine çağıran ekran listeyi yeniden çekiyor.
 */
export const archiveFuar = createAsyncThunk(
  'fuar/archive',
  async (fuarId, { rejectWithValue }) => {
    try {
      await fuarService.archiveFuar(fuarId);
      return fuarId;
    } catch (error) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const unarchiveFuar = createAsyncThunk(
  'fuar/unarchive',
  async (fuarId, { rejectWithValue }) => {
    try {
      await fuarService.unarchiveFuar(fuarId);
      return fuarId;
    } catch (error) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

/**
 * Initial State
 */
const initialState = {
  list: [],
  selectedFuar: null,
  loading: false,
  detailLoading: false,
  error: null,
  success: null,
  // #718: the per-role Kurulum hours of the LAST save were refused: { fuarId, message } (the fair itself was saved); null otherwise.
  kurulumHata: null,
  pagination: {
    currentPage: 0,
    pageSize: 25,
    totalElements: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    languageId: 1,
  },
};

/**
 * Fuar Slice
 */
const fuarSlice = createSlice({
  name: 'fuar',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearKurulumHata: (state) => {
      state.kurulumHata = null;
    },
    resetFuarState: (state) => {
      state.list = [];
      state.selectedFuar = null;
      state.loading = false;
      state.error = null;
      state.success = null;
      state.kurulumHata = null;
    },
  },
  extraReducers: (builder) => {
    // fetchFuarList
    builder
      .addCase(fetchFuarList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFuarList.fulfilled, (state, action) => {
        state.loading = false;
        // /fuar/list sayfalamıyor: düz dizi. Sayfalama/arama FuarListPage'de yapılıyor;
        // pagination alanı eski tüketiciler için tutarlı bırakılıyor.
        state.list = Array.isArray(action.payload) ? action.payload : [];
        state.pagination = {
          currentPage: 0,
          pageSize: state.list.length,
          totalElements: state.list.length,
          totalPages: 1,
        };
      })
      .addCase(fetchFuarList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // fetchFuarDetail
    builder
      .addCase(fetchFuarDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchFuarDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedFuar = action.payload;
      })
      .addCase(fetchFuarDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      });

    // createFuar
    builder
      .addCase(createFuar.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.kurulumHata = null;
      })
      .addCase(createFuar.fulfilled, (state, action) => {
        state.loading = false;
        state.kurulumHata = action.payload?.kurulumHata ?? null; // #718
        // Global şablonun kopyalanıp kopyalanmadığını mesajda söylüyoruz: sessizce
        // atlanırsa organizatör kuralların yazıldığını sanır ve fuar kuralsız açılır.
        const gd = action.payload?.globalDefaults;
        state.success = (gd?.applied
          ? i18n.t("services:fuarSlice.fuarOlusturulduVeGlobalKural")
          : i18n.t("services:fuarSlice.fuarBasariylaOlusturuldu", { v: gd?.reason ? i18n.t("services:fuarSlice.globalSablonUygulanmadi", { reason: gd.reason }) : '' }))
          + hallLinkNote(action.payload?.hallLink); // #192
      })
      .addCase(createFuar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // updateFuar
    builder
      .addCase(updateFuar.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.kurulumHata = null;
      })
      .addCase(updateFuar.fulfilled, (state, action) => {
        state.loading = false;
        state.kurulumHata = action.payload?.kurulumHata ?? null; // #718
        // WAS: state.success = 'Fuar başarıyla güncellendi';
        state.success = i18n.t("services:fuarSlice.fuarBasariylaGuncellendi") + venueCleanupNote(action.payload?.venueCleanup); // #192
        // Liste satırı (FuarListDTO) ile update yanıtı aynı şekil değil — satırı ezmek
        // yerine liste bir sonraki açılışta yeniden çekiliyor.
      })
      .addCase(updateFuar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // deleteFuar
    builder
      .addCase(deleteFuar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFuar.fulfilled, (state, action) => {
        state.loading = false;
        state.success = i18n.t("services:fuarSlice.fuarBasariylaSilindi");
        state.list = state.list.filter((f) => f.fuarId !== action.payload);
      })
      .addCase(deleteFuar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // fetchFuarStatus
    builder
      .addCase(fetchFuarStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFuarStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedFuar) {
          state.selectedFuar.status = action.payload;
        }
      })
      .addCase(fetchFuarStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // archiveFuar — optimistic: we know exactly what changed (-> 'ARCHIVED').
    builder
      .addCase(archiveFuar.fulfilled, (state, action) => {
        const row = state.list.find((f) => f.fuarId === action.payload);
        if (row) { row.fuarGroup = 'ARCHIVED'; row.archived = true; }
        state.success = i18n.t("services:fuarSlice.fuarArsivlendi");
      })
      .addCase(archiveFuar.rejected, (state, action) => {
        state.error = action.payload;
      });

    // unarchiveFuar — NOT optimistic: which group it falls back into is date logic that
    // only the backend computes (see thunk comment). Caller re-fetches the list.
    builder
      .addCase(unarchiveFuar.fulfilled, (state) => {
        state.success = i18n.t("services:fuarSlice.fuarArsivdenCikarildi");
      })
      .addCase(unarchiveFuar.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearError, clearSuccess, clearKurulumHata, resetFuarState } = fuarSlice.actions;
export default fuarSlice.reducer;

